import * as Y from "yjs";
import { IndexeddbPersistence } from "y-indexeddb";
import { WebrtcProvider, type Awareness } from "y-webrtc";
import { FrameworkManager } from "../domain/framework-manager";
import { exportLinearIssues } from "../domain/linear-exporter";
import { exportGridCsv, importGridCsv } from "../domain/csv";
import type { FrameworkId, RelationPatch, SyncrelState } from "../domain/types";
import {
  addEntity,
  addSpecPoll,
  addSpecPollOption,
  linkEntities,
  markFrameworkWelcomeSeen,
  moveEntity,
  patchEntity,
  patchRelation,
  patchSpecPollOption,
  patchSpecPollQuestion,
  renameWorkspace,
  removeEntity,
  removeRelation,
  removeSpecPoll,
  removeSpecPollOption,
  selectEntity,
  selectRelation,
  addSchemaField,
  addChatMessage,
  voteSpecPoll,
  type ChatMessageInput,
  type EntityInput,
  type EntityPatch,
  type RelationInput,
  type SchemaFieldInput,
} from "./mutations";
import { createSeedState } from "./seed";
import { loadLatestSnapshot, saveSnapshot } from "./persistence";
import { WebsocketSyncProvider } from "./websocket-sync";
import { migrateLegacyFieldReferences } from "../domain/state-migrations";
import { ensureWorkspaceShareKeys, workspaceShareKeysFromUrl } from "../domain/share";
import {
  loadRemoteRoomSnapshot,
  remoteAccessFromUrl,
  saveRemotePollVote,
  saveRemoteRoomSnapshot,
} from "./remote-persistence";

type Listener = (state: SyncrelState) => void;
type PresenceListener = (activeUserCount: number) => void;
interface SyncrelStoreOptions {
  readOnly?: boolean;
}

interface SetStateOptions {
  allowReadOnlyWrite?: boolean;
}

const ROOT_KEY = "state";
const REMOTE_SNAPSHOT_DELAY = 8000;

export class SyncrelStore {
  private readonly doc: Y.Doc;
  private readonly root: Y.Map<unknown>;
  private readonly persistence: IndexeddbPersistence;
  private readonly webrtc: WebrtcProvider | null;
  private readonly awareness: Awareness | null;
  private readonly websocket: WebsocketSyncProvider | null;
  private readonly undoManager: Y.UndoManager;
  private readOnly: boolean;
  private readonly listeners = new Set<Listener>();
  private readonly presenceListeners = new Set<PresenceListener>();
  private readonly frameworkManager = new FrameworkManager();
  private snapshotTimer: number | null = null;
  private remoteSnapshotTimer: number | null = null;

  private readonly onPresenceChange = (): void => this.emitPresence();

  private constructor(
    doc: Y.Doc,
    persistence: IndexeddbPersistence,
    webrtc: WebrtcProvider | null,
    websocket: WebsocketSyncProvider | null,
    options: SyncrelStoreOptions = {},
  ) {
    this.doc = doc;
    this.root = this.doc.getMap("syncrel");
    this.persistence = persistence;
    this.webrtc = webrtc;
    this.awareness = webrtc?.awareness ?? null;
    this.websocket = websocket;
    this.readOnly = options.readOnly === true;
    this.undoManager = new Y.UndoManager(this.root, { captureTimeout: 0 });
    this.root.observe(() => this.emit());
    this.awareness?.setLocalStateField("syncrel", { active: true, joinedAt: Date.now() });
    this.awareness?.on("change", this.onPresenceChange);
  }

  static async create(room = roomFromUrl(), options: SyncrelStoreOptions = {}): Promise<SyncrelStore> {
    const doc = new Y.Doc();
    const persistence = new IndexeddbPersistence(`syncrel:${room}`, doc);
    const webrtc = createWebrtcProvider(room, doc);
    const websocket = createWebsocketProvider(room, doc);
    const store = new SyncrelStore(doc, persistence, webrtc, websocket, options);

    await new Promise<void>((resolve) => {
      let resolved = false;
      const done = () => {
        if (resolved) return;
        resolved = true;
        resolve();
      };
      persistence.on("synced", done);
      window.setTimeout(done, 650);
    });

    const shareKeys = workspaceShareKeysFromUrl();
    const remoteAccess = remoteAccessFromUrl();
    const remoteSnapshot = await loadRemoteRoomSnapshot(room, remoteAccess);
    if (!store.root.has(ROOT_KEY)) {
      const localSnapshot = await loadLatestSnapshot(room);
      const snapshot = newestSnapshot(remoteSnapshot, localSnapshot);
      const seed = createSeedState();
      store.commitState(ensureWorkspaceShareKeys(snapshot ?? { ...seed, workspace: { ...seed.workspace, room } }, shareKeys));
    } else {
      const state = store.getState();
      const secured = ensureWorkspaceShareKeys(newestSnapshot(remoteSnapshot, state) ?? state, shareKeys);
      if (secured !== state) store.commitState(secured);
    }
    store.undoManager.clear();

    return store;
  }

  getState(): SyncrelState {
    const value = this.root.get(ROOT_KEY);
    if (value && typeof value === "object") return migrateLegacyFieldReferences(value as SyncrelState);
    return createSeedState();
  }

  setReadOnly(readOnly: boolean): void {
    this.readOnly = readOnly;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.getState());
    return () => this.listeners.delete(listener);
  }

  activeUserCount(): number {
    return Math.max(1, this.awareness?.getStates().size ?? 1);
  }

  subscribePresence(listener: PresenceListener): () => void {
    this.presenceListeners.add(listener);
    listener(this.activeUserCount());
    return () => this.presenceListeners.delete(listener);
  }

  setState(state: SyncrelState, options: SetStateOptions = {}): void {
    if (this.readOnly && !options.allowReadOnlyWrite) return;
    this.commitState(state);
  }

  private commitState(state: SyncrelState): void {
    this.root.set(ROOT_KEY, state);
    this.queueSnapshot();
  }

  select(entityId: string | null): void {
    this.setState(selectEntity(this.getState(), entityId), {
      allowReadOnlyWrite: true,
    });
  }

  patchEntity(entityId: string, patch: EntityPatch): void {
    this.setState(patchEntity(this.getState(), entityId, patch));
  }

  renameWorkspace(name: string): void {
    this.setState(renameWorkspace(this.getState(), name));
  }

  moveEntity(entityId: string, position: { x: number; y: number }): void {
    this.setState(moveEntity(this.getState(), entityId, position));
  }

  addEntity(input: EntityInput = {}): void {
    this.setState(addEntity(this.getState(), input));
  }

  removeEntity(entityId: string): void {
    this.setState(removeEntity(this.getState(), entityId));
  }

  linkEntities(input: RelationInput): void {
    this.setState(linkEntities(this.getState(), input));
  }

  selectRelation(relationId: string | null): void {
    this.setState(selectRelation(this.getState(), relationId));
  }

  patchRelation(relationId: string, patch: RelationPatch): void {
    this.setState(patchRelation(this.getState(), relationId, patch));
  }

  removeRelation(relationId: string): void {
    this.setState(removeRelation(this.getState(), relationId));
  }

  addSpecPoll(entityId: string): void {
    this.setState(addSpecPoll(this.getState(), entityId));
  }

  patchSpecPollQuestion(entityId: string, pollId: string, question: string): void {
    this.setState(patchSpecPollQuestion(this.getState(), entityId, pollId, question));
  }

  patchSpecPollOption(entityId: string, pollId: string, optionId: string, label: string): void {
    this.setState(patchSpecPollOption(this.getState(), entityId, pollId, optionId, label));
  }

  addSpecPollOption(entityId: string, pollId: string): void {
    this.setState(addSpecPollOption(this.getState(), entityId, pollId));
  }

  removeSpecPollOption(entityId: string, pollId: string, optionId: string): void {
    this.setState(removeSpecPollOption(this.getState(), entityId, pollId, optionId));
  }

  removeSpecPoll(entityId: string, pollId: string): void {
    this.setState(removeSpecPoll(this.getState(), entityId, pollId));
  }

  voteSpecPoll(entityId: string, pollId: string, optionId: string, viewerId: string): void {
    this.setState(voteSpecPoll(this.getState(), entityId, pollId, optionId, viewerId), {
      allowReadOnlyWrite: true,
    });
    const state = this.getState();
    void saveRemotePollVote(state.workspace.room, { entityId, pollId, optionId, viewerId }, remoteAccessFromUrl());
  }

  addChatMessage(input: ChatMessageInput): void {
    this.setState(addChatMessage(this.getState(), input));
  }

  addSchemaField(input: SchemaFieldInput): void {
    this.setState(addSchemaField(this.getState(), input));
  }

  applyFramework(templateId: FrameworkId): void {
    this.setState(this.frameworkManager.applyTemplate(this.getState(), templateId));
  }

  enableFramework(templateId: FrameworkId): void {
    this.setState(this.frameworkManager.enableTemplate(this.getState(), templateId));
  }

  removeFramework(templateId: FrameworkId): void {
    this.setState(this.frameworkManager.removeTemplate(this.getState(), templateId));
  }

  markFrameworkWelcomeSeen(templateId: FrameworkId): void {
    this.setState(markFrameworkWelcomeSeen(this.getState(), templateId));
  }

  exportLinear(entityIds: string[]): string {
    return JSON.stringify(exportLinearIssues(this.getState(), entityIds), null, 2);
  }

  exportCsv(entityIds: string[]): string {
    return exportGridCsv(this.getState(), entityIds);
  }

  importCsv(csv: string): void {
    this.setState(importGridCsv(this.getState(), csv));
  }

  undo(): void {
    if (this.readOnly) return;
    this.undoManager.undo();
    this.queueSnapshot();
  }

  redo(): void {
    if (this.readOnly) return;
    this.undoManager.redo();
    this.queueSnapshot();
  }

  destroy(): void {
    if (this.snapshotTimer) window.clearTimeout(this.snapshotTimer);
    if (this.remoteSnapshotTimer) window.clearTimeout(this.remoteSnapshotTimer);
    this.undoManager.destroy();
    this.awareness?.off("change", this.onPresenceChange);
    this.persistence.destroy();
    this.webrtc?.destroy();
    this.websocket?.destroy();
    this.doc.destroy();
    this.listeners.clear();
    this.presenceListeners.clear();
  }

  private emit(): void {
    const state = this.getState();
    for (const listener of this.listeners) listener(state);
  }

  private emitPresence(): void {
    const count = this.activeUserCount();
    for (const listener of this.presenceListeners) listener(count);
  }

  private queueSnapshot(): void {
    if (this.snapshotTimer) window.clearTimeout(this.snapshotTimer);
    this.snapshotTimer = window.setTimeout(() => {
      const state = this.getState();
      void saveSnapshot(state.workspace.room, state);
    }, 400);
    if (!this.readOnly) this.queueRemoteSnapshot();
  }

  private queueRemoteSnapshot(): void {
    if (this.remoteSnapshotTimer) window.clearTimeout(this.remoteSnapshotTimer);
    this.remoteSnapshotTimer = window.setTimeout(() => {
      const state = this.getState();
      void saveRemoteRoomSnapshot(state);
    }, REMOTE_SNAPSHOT_DELAY);
  }
}

const newestSnapshot = (
  first: SyncrelState | null | undefined,
  second: SyncrelState | null | undefined,
): SyncrelState | null => {
  if (!first) return second ?? null;
  if (!second) return first;
  return first.workspace.updatedAt >= second.workspace.updatedAt ? first : second;
};

const createWebrtcProvider = (room: string, doc: Y.Doc): WebrtcProvider | null => {
  if (!shouldEnableWebrtc()) return null;
  try {
    return new WebrtcProvider(`syncrel:${room}`, doc, {
      signaling: ["wss://signaling.yjs.dev", "wss://y-webrtc-signaling-eu.herokuapp.com"],
    });
  } catch {
    return null;
  }
};

const shouldEnableWebrtc = (): boolean => {
  const url = new URL(window.location.href);
  const p2p = url.searchParams.get("p2p");
  if (p2p === "1") return true;
  if (p2p === "0") return false;
  return !["127.0.0.1", "localhost", "::1"].includes(window.location.hostname);
};

// Opt-in central-relay sync (Google-Docs-style) via ?wss=wss://host. Off by
// default, so the static/local-first posture is preserved unless requested.
const createWebsocketProvider = (room: string, doc: Y.Doc): WebsocketSyncProvider | null => {
  try {
    const url = new URL(window.location.href).searchParams.get("wss");
    if (!url || !/^wss?:\/\//.test(url)) return null;
    return new WebsocketSyncProvider(url, `syncrel:${room}`, doc);
  } catch {
    return null;
  }
};

const roomFromUrl = (): string => {
  const url = new URL(window.location.href);
  const param = url.searchParams.get("room");
  if (param && /^[a-zA-Z0-9_-]{1,64}$/.test(param)) return param;
  const room = "syncrel-start";
  url.searchParams.set("room", room);
  window.history.replaceState(null, "", url.toString());
  return room;
};
